import { useEffect, useState } from 'react'
import { useLiveQuery } from 'dexie-react-hooks'
import { useClub } from './ClubProvider.tsx'
import { onBackOnScreen, onScreen } from '../platform/visibility.ts'

/** The club's logo (core.club_branding, mirrored): fetched once from storage, then kept on this device as a data URL so the header has it with no signal. `null`: the club has none
 *  (the app's own emblem is shown). The last logo seen is also kept for the sign-in page, which does not know the club yet. */
export interface BrandingRow { id: string; logo_sha256: string | null; logo_storage_key: string | null; logo_mime: string | null; revision: number }
const cacheKey = (org: string) => `gk.logo.${org}`
export const LAST_LOGO_KEY = 'gk.logo.last'
export function readCachedLogo(org: string): { sha: string; url: string } | null {
  try { const s = localStorage.getItem(cacheKey(org)); return s ? (JSON.parse(s) as { sha: string; url: string }) : null } catch { return null }
}
const LOGO_EVENT = 'gk-logo'
export function writeCachedLogo(org: string, sha: string, url: string): void {
  try { localStorage.setItem(cacheKey(org), JSON.stringify({ sha, url })); localStorage.setItem(LAST_LOGO_KEY, url) } catch { /* private mode or full: the logo is fetched again next time */ }
  window.dispatchEvent(new Event(LOGO_EVENT))                     // the header of this device shows a logo it has just saved at once
}
/** How long the header waits before it asks storage again for a logo it could not fetch (the row can arrive before the bytes are uploaded, or with no signal). */
export const LOGO_RETRY_MS = 15_000
/** Calls `retry` after `ms`, but never while the app is off screen (the phone in a pocket, the native app in the background, whose Android shell keeps
 *  its page running, M19b): a retry that falls due off screen waits for the way back. Returns the cancellation. */
export function retryOnScreen(retry: () => void, ms = LOGO_RETRY_MS): () => void {
  let unwatch: (() => void) | undefined
  const due = () => {
    if (onScreen()) { retry(); return }
    unwatch = onBackOnScreen(() => { unwatch?.(); unwatch = undefined; retry() })
  }
  const timer = setTimeout(due, ms)
  return () => { clearTimeout(timer); unwatch?.() }
}
export function lastLogo(): string | null { try { return localStorage.getItem(LAST_LOGO_KEY) } catch { return null } }
export const blobToDataUrl = (b: Blob): Promise<string> => new Promise((resolve, reject) => { const r = new FileReader(); r.onload = () => resolve(String(r.result)); r.onerror = () => reject(r.error); r.readAsDataURL(b) })

export function useBranding(): BrandingRow | null | undefined {
  const { db } = useClub()
  const rows = useLiveQuery(() => db.club_branding.toArray(), [db]) as BrandingRow[] | undefined
  return rows === undefined ? undefined : (rows[0] ?? null)
}

export function useClubLogo(): string | null {
  const { transport, org } = useClub()
  const b = useBranding()
  const [url, setUrl] = useState<string | null>(() => readCachedLogo(org.org_id)?.url ?? null)
  const [attempt, setAttempt] = useState(0)
  const sha = b?.logo_sha256 ?? null, key = b?.logo_storage_key ?? null
  const loading = b === undefined
  useEffect(() => {
    const again = () => setAttempt((n) => n + 1)
    window.addEventListener(LOGO_EVENT, again)
    return () => window.removeEventListener(LOGO_EVENT, again)
  }, [])
  useEffect(() => {
    if (loading) return
    if (!sha || !key) { setUrl(null); return }
    const cached = readCachedLogo(org.org_id)
    if (cached?.sha === sha) { setUrl(cached.url); return }
    let live = true; let cancelRetry: (() => void) | undefined
    void transport.attachmentUrl(key)
      .then((u) => fetch(u))
      .then((r) => (r.ok ? r.blob() : Promise.reject(new Error(`logo ${r.status}`))))
      .then(blobToDataUrl)
      .then((d) => { writeCachedLogo(org.org_id, sha, d); if (live) setUrl(d) })
      // not uploaded yet, or offline: the emblem (or the previous logo) stays meanwhile; asked again in LOGO_RETRY_MS, never while off screen
      .catch(() => { if (live) cancelRetry = retryOnScreen(() => setAttempt((n) => n + 1)) })
    return () => { live = false; cancelRetry?.() }
  }, [loading, sha, key, org.org_id, transport, attempt])
  return url
}
